import React, { useEffect, useState, useCallback } from 'react';
import {
  Table, Button, Input, Select, Tag, Space, Modal, Form, DatePicker,
  message, Typography, Card, Row, Col, Statistic, Popconfirm, Timeline, Descriptions,
} from 'antd';
import {
  PlusOutlined, SearchOutlined, EditOutlined, DeleteOutlined,
  ProjectOutlined, ClockCircleOutlined, SyncOutlined, CheckCircleOutlined,
  EyeOutlined, FileTextOutlined,
} from '@ant-design/icons';
import { projectApi, customerApi, contractApi, userApi } from '../../services/api';
import useAuthStore from '../../store/authStore';
import dayjs from 'dayjs';

const { Title, Text } = Typography;
const { Option } = Select;
const { TextArea } = Input;

const STATUS_OPTIONS = ['待启动', '进行中', '待验收', '已完成'];
const STATUS_COLORS = {
  '待启动': 'default',
  '进行中': 'processing',
  '待验收': 'warning',
  '已完成': 'success',
};
const LOG_TYPES = ['巡检', '维护', '故障处理', '安装调试', '培训', '其他'];

// ==================== 看板列配置 ====================
const COLUMNS_CONFIG = [
  { status: '待启动', color: '#bfbfbf', icon: <ClockCircleOutlined /> },
  { status: '进行中', color: '#1890ff', icon: <SyncOutlined spin /> },
  { status: '待验收', color: '#faad14', icon: <EyeOutlined /> },
  { status: '已完成', color: '#52c41a', icon: <CheckCircleOutlined /> },
];

export default function Projects() {
  const user = useAuthStore((s) => s.user);
  const [projects, setProjects] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(false);

  // 筛选
  const [searchText, setSearchText] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  // 新建/编辑 Modal
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form] = Form.useForm();
  const [formLoading, setFormLoading] = useState(false);

  // 详情 Drawer
  const [detailOpen, setDetailOpen] = useState(false);
  const [activeProject, setActiveProject] = useState(null);

  // 服务日志
  const [logModalOpen, setLogModalOpen] = useState(false);
  const [logForm] = Form.useForm();
  const [logLoading, setLogLoading] = useState(false);

  // 关联数据
  const [customers, setCustomers] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [salesList, setSalesList] = useState([]);

  const fetchProjects = useCallback(async () => {
    setLoading(true);
    try {
      const params = { pageSize: 100 };
      if (statusFilter) params.status = statusFilter;
      const res = await projectApi.list(params);
      setProjects(res.data || []);
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  const fetchStats = useCallback(async () => {
    try {
      const res = await projectApi.stats();
      setStats(res);
    } catch { /* 静默 */ }
  }, []);

  useEffect(() => { fetchProjects(); fetchStats(); }, [fetchProjects, fetchStats]);

  // 加载关联数据
  useEffect(() => {
    if (user?.role !== 'sales') {
      customerApi.list({ pageSize: 200 }).then(res => setCustomers(res.data || [])).catch(() => {});
      contractApi.list({ pageSize: 200 }).then(res => setContracts(res.data || [])).catch(() => {});
      userApi.list().then(setSalesList).catch(() => {});
    }
  }, [user]);

  // ==================== 看板视图渲染 ====================
  const filteredProjects = projects.filter(p => {
    if (!searchText) return true;
    const kw = searchText.toLowerCase();
    return (
      p.name?.toLowerCase().includes(kw) ||
      p.customer?.name?.toLowerCase().includes(kw) ||
      p.customer?.company?.toLowerCase().includes(kw)
    );
  });

  const groupedByStatus = COLUMNS_CONFIG.map(col => ({
    ...col,
    items: filteredProjects.filter(p => p.status === col.status),
  }));

  // ==================== 操作 ====================
  const openCreate = () => {
    setEditing(null);
    form.resetFields();
    form.setFieldsValue({ status: '待启动' });
    setModalOpen(true);
  };

  const openEdit = (record) => {
    setEditing(record);
    form.setFieldsValue({
      ...record,
      startAt: record.startAt ? dayjs(record.startAt) : null,
      endAt: record.endAt ? dayjs(record.endAt) : null,
    });
    setModalOpen(true);
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      setFormLoading(true);

      const payload = {
        ...values,
        startAt: values.startAt?.toISOString(),
        endAt: values.endAt?.toISOString(),
      };

      if (editing) {
        await projectApi.update(editing.id, payload);
        message.success('项目已更新');
      } else {
        await projectApi.create(payload);
        message.success('项目已创建');
      }
      setModalOpen(false);
      fetchProjects();
      fetchStats();
    } catch (e) {
      if (e?.errorFields) return; // 表单验证失败
      message.error(e?.error || '操作失败');
    } finally {
      setFormLoading(false);
    }
  };

  const handleDelete = (id) => {
    Modal.confirm({
      title: '确认删除该项目？',
      content: '删除后所有服务日志也将一并删除。',
      okText: '确认删除',
      okButtonProps: { danger: true },
      cancelText: '取消',
      onOk: async () => {
        await projectApi.delete(id);
        message.success('已删除');
        fetchProjects();
        fetchStats();
      },
    });
  };

  // ==================== 快速状态切换 ====================
  const changeStatus = async (project, newStatus) => {
    try {
      await projectApi.update(project.id, { status: newStatus });
      message.success(`项目状态已更新为「${newStatus}」`);
      fetchProjects();
      fetchStats();
      if (activeProject?.id === project.id) {
        setActiveProject(prev => ({ ...prev, status: newStatus }));
      }
    } catch (e) {
      message.error(e?.error || '操作失败');
    }
  };

  // ==================== 详情 ====================
  const openDetail = async (record) => {
    setActiveProject(record);
    setDetailOpen(true);
    try {
      const detail = await projectApi.get(record.id);
      setActiveProject(detail);
    } catch (e) {
      message.error(e?.error || '加载详情失败');
    }
  };

  // ==================== 服务日志 ====================
  const openLogModal = () => {
    logForm.resetFields();
    logForm.setFieldsValue({ serviceAt: dayjs() });
    setLogModalOpen(true);
  };

  const handleAddLog = async () => {
    try {
      const values = await logForm.validateFields();
      setLogLoading(true);
      await projectApi.addLog(activeProject.id, {
        ...values,
        serviceAt: values.serviceAt?.toISOString(),
      });
      message.success('服务日志已添加');
      setLogModalOpen(false);
      // 刷新详情
      const detail = await projectApi.get(activeProject.id);
      setActiveProject(detail);
    } catch (e) {
      if (e?.errorFields) return;
      message.error(e?.error || '操作失败');
    } finally {
      setLogLoading(false);
    }
  };

  const handleDeleteLog = async (logId) => {
    try {
      await projectApi.deleteLog(activeProject.id, logId);
      message.success('日志已删除');
      const detail = await projectApi.get(activeProject.id);
      setActiveProject(detail);
    } catch (e) {
      message.error(e?.error || '操作失败');
    }
  };

  // ==================== 表格列 ====================
  const columns = [
    {
      title: '项目名称', dataIndex: 'name', key: 'name',
      render: (name, r) => (
        <a onClick={() => openDetail(r)} style={{ fontWeight: 500 }}>{name}</a>
      ),
    },
    {
      title: '客户', dataIndex: 'customer', key: 'customer',
      render: (c) => c?.name || '—',
    },
    {
      title: '关联合同', dataIndex: 'contract', key: 'contract',
      render: (c) => c ? <Tag>{c.code}</Tag> : <Text type="secondary">无</Text>,
    },
    {
      title: '状态', dataIndex: 'status', key: 'status',
      render: (v) => <Tag color={STATUS_COLORS[v]}>{v}</Tag>,
    },
    {
      title: '负责人', dataIndex: 'user', key: 'user',
      render: (u) => u?.name || '—',
    },
    {
      title: '起止时间', key: 'date',
      render: (_, r) => (
        <Text type="secondary" style={{ fontSize: 12 }}>
          {r.startAt ? dayjs(r.startAt).format('MM-DD') : '—'} ~ {r.endAt ? dayjs(r.endAt).format('MM-DD') : '—'}
        </Text>
      ),
    },
    {
      title: '服务记录', dataIndex: '_count', key: 'logs',
      render: (c) => c?.logs || 0,
    },
    {
      title: '操作', key: 'actions',
      render: (_, r) => (
        <Space>
          <Button type="link" size="small" icon={<EditOutlined />}
            onClick={() => openEdit(r)}
            disabled={user?.role === 'sales' && r.userId !== user?.id}>
            编辑
          </Button>
          <Popconfirm title="确认删除？" onConfirm={() => handleDelete(r.id)}
            disabled={user?.role === 'sales'}>
            <Button type="link" size="small" danger icon={<DeleteOutlined />}
              disabled={user?.role === 'sales'}>
              删除
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <Title level={4} style={{ margin: 0 }}>
          <ProjectOutlined style={{ marginRight: 8 }} />项目看板
        </Title>
        {(user?.role === 'admin' || user?.role === 'manager') && (
          <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>新建项目</Button>
        )}
      </div>

      {/* ===== 统计卡片 ===== */}
      <Row gutter={16} style={{ marginBottom: 16 }}>
        {[
          { label: '全部项目', value: stats.total || 0, color: '#1890ff' },
          { label: '待启动', value: stats.pending || 0, color: '#bfbfbf' },
          { label: '进行中', value: stats.active || 0, color: '#1890ff' },
          { label: '待验收', value: stats.inspection || 0, color: '#faad14' },
          { label: '已完成', value: stats.completed || 0, color: '#52c41a' },
        ].map((item, idx) => (
          <Col xs={24} sm={12} md={Math.floor(24 / 5)} key={idx}>
            <Card size="small" style={{ textAlign: 'center' }}>
              <Statistic title={item.label} value={item.value} valueStyle={{ color: item.color, fontSize: 24 }} />
            </Card>
          </Col>
        ))}
      </Row>

      {/* ===== 筛选栏 ===== */}
      <Space style={{ marginBottom: 16 }}>
        <Input
          placeholder="搜索项目名称/客户"
          prefix={<SearchOutlined />}
          value={searchText}
          onChange={e => setSearchText(e.target.value)}
          allowClear
          style={{ width: 220 }}
        />
        <Select
          placeholder="筛选状态"
          value={statusFilter}
          onChange={setStatusFilter}
          allowClear
          style={{ width: 130 }}
        >
          {STATUS_OPTIONS.map(s => <Option key={s} value={s}>{s}</Option>)}
        </Select>
      </Space>

      {/* ===== 看板视图 (四列) ===== */}
      <div style={{ display: 'flex', gap: 12, overflowX: 'auto', paddingBottom: 8 }}>
        {groupedByStatus.map((col, colIdx) => (
          <div key={colIdx} style={{
            flex: 1, minWidth: 240, maxWidth: 360,
            background: '#fafafa', borderRadius: 12, padding: 12,
            borderTop: `3px solid ${col.color}`,
          }}>
            <div style={{
              display: 'flex', alignItems: 'center', gap: 6,
              marginBottom: 12, paddingBottom: 8,
              borderBottom: '1px solid #e8e8e8',
            }}>
              <span style={{ color: col.color, fontSize: 16 }}>{col.icon}</span>
              <Text strong>{col.status}</Text>
              <Tag style={{ marginLeft: 'auto' }}>{col.items.length}</Tag>
            </div>

            {col.items.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '24px 0', color: '#ccc' }}>
                <Text type="secondary">暂无项目</Text>
              </div>
            ) : (
              col.items.map(project => (
                <Card
                  key={project.id}
                  size="small"
                  hoverable
                  style={{ marginBottom: 8, cursor: 'pointer' }}
                  onClick={() => openDetail(project)}
                >
                  <div style={{ fontWeight: 500, marginBottom: 4 }}>{project.name}</div>
                  <div style={{ fontSize: 12, color: '#888', marginBottom: 4 }}>
                    {project.customer?.name}
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Text type="secondary" style={{ fontSize: 11 }}>
                      {project.user?.name}
                    </Text>
                    <Space size={4}>
                      {project._count?.logs > 0 && (
                        <Tag style={{ fontSize: 11, margin: 0 }} color="blue">
                          <FileTextOutlined /> {project._count.logs}
                        </Tag>
                      )}
                      {project.contract && (
                        <Tag style={{ fontSize: 11, margin: 0 }} color="green">{project.contract.code}</Tag>
                      )}
                    </Space>
                  </div>
                </Card>
              ))
            )}
          </div>
        ))}
      </div>

      {/* ===== 表格视图（可选切换） ===== */}
      <div style={{ marginTop: 24 }}>
        <Title level={5}>项目列表</Title>
        <Table
          dataSource={filteredProjects}
          columns={columns}
          rowKey="id"
          loading={loading}
          pagination={{ pageSize: 20, showTotal: t => `共 ${t} 个项目` }}
          size="middle"
        />
      </div>

      {/* ===== 新建/编辑 Modal ===== */}
      <Modal
        title={editing ? '编辑项目' : '新建项目'}
        open={modalOpen}
        onOk={handleSave}
        onCancel={() => setModalOpen(false)}
        confirmLoading={formLoading}
        width={560}
        destroyOnClose
      >
        <Form form={form} layout="vertical">
          <Form.Item name="name" label="项目名称" rules={[{ required: true, message: '请输入项目名称' }]}>
            <Input placeholder="例如：XX公司网络升级项目" />
          </Form.Item>
          <Form.Item name="customerId" label="关联客户" rules={[{ required: true, message: '请选择客户' }]}>
            <Select placeholder="选择客户" showSearch optionFilterProp="label"
              options={customers.map(c => ({ label: c.name + (c.company ? ` (${c.company})` : ''), value: c.id }))}
            />
          </Form.Item>
          <Form.Item name="contractId" label="关联合同（可选）">
            <Select placeholder="选择合同" allowClear showSearch optionFilterProp="label"
              options={contracts.map(c => ({ label: `${c.code} - ${c.customer?.name || ''}`, value: c.id }))}
            />
          </Form.Item>
          {editing && user?.role === 'admin' && (
            <Form.Item name="userId" label="负责人">
              <Select placeholder="选择负责人" allowClear
                options={salesList.map(s => ({ label: s.name, value: s.id }))}
              />
            </Form.Item>
          )}
          <Space size={16}>
            <Form.Item name="startAt" label="开始日期">
              <DatePicker style={{ width: 200 }} />
            </Form.Item>
            <Form.Item name="endAt" label="结束日期">
              <DatePicker style={{ width: 200 }} />
            </Form.Item>
          </Space>
          <Form.Item name="status" label="项目状态">
            <Select>
              {STATUS_OPTIONS.map(s => <Option key={s} value={s}>{s}</Option>)}
            </Select>
          </Form.Item>
          <Form.Item name="notes" label="备注">
            <TextArea rows={3} placeholder="项目备注说明" />
          </Form.Item>
        </Form>
      </Modal>

      {/* ===== 项目详情 Drawer ===== */}
      <Modal
        title={null}
        open={detailOpen}
        onCancel={() => setDetailOpen(false)}
        footer={null}
        width={720}
        destroyOnClose
      >
        {activeProject && (
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <Title level={4} style={{ margin: 0 }}>{activeProject.name}</Title>
              <Tag color={STATUS_COLORS[activeProject.status]} style={{ fontSize: 14, padding: '2px 12px' }}>
                {activeProject.status}
              </Tag>
            </div>

            <Descriptions bordered size="small" column={2} style={{ marginBottom: 16 }}>
              <Descriptions.Item label="客户">{activeProject.customer?.name}</Descriptions.Item>
              <Descriptions.Item label="负责人">{activeProject.user?.name}</Descriptions.Item>
              <Descriptions.Item label="关联合同">
                {activeProject.contract ? (
                  <Tag color="green">{activeProject.contract.code} ({activeProject.contract.status})</Tag>
                ) : '—'}
              </Descriptions.Item>
              <Descriptions.Item label="创建时间">
                {dayjs(activeProject.createdAt).format('YYYY-MM-DD HH:mm')}
              </Descriptions.Item>
              <Descriptions.Item label="开始日期">
                {activeProject.startAt ? dayjs(activeProject.startAt).format('YYYY-MM-DD') : '—'}
              </Descriptions.Item>
              <Descriptions.Item label="结束日期">
                {activeProject.endAt ? dayjs(activeProject.endAt).format('YYYY-MM-DD') : '—'}
              </Descriptions.Item>
              {activeProject.notes && (
                <Descriptions.Item label="备注" span={2}>{activeProject.notes}</Descriptions.Item>
              )}
            </Descriptions>

            {/* 快速状态切换 */}
            {(user?.role === 'admin' || user?.role === 'manager') && (
              <div style={{ marginBottom: 16, padding: '8px 12px', background: '#fafafa', borderRadius: 8 }}>
                <Text style={{ marginRight: 12 }}>状态切换：</Text>
                <Space>
                  {STATUS_OPTIONS.map(s => (
                    <Button
                      key={s}
                      size="small"
                      type={activeProject.status === s ? 'primary' : 'default'}
                      onClick={() => changeStatus(activeProject, s)}
                    >
                      {s}
                    </Button>
                  ))}
                </Space>
              </div>
            )}

            {/* 服务日志 */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <Title level={5} style={{ margin: 0 }}>
                <FileTextOutlined style={{ marginRight: 6 }} />服务日志
              </Title>
              <Button type="link" icon={<PlusOutlined />} onClick={openLogModal}>添加日志</Button>
            </div>

            {activeProject.logs?.length > 0 ? (
              <Timeline
                items={activeProject.logs.map(log => ({
                  color: log.type === '故障处理' ? 'red' : log.type === '巡检' ? 'blue' : 'green',
                  children: (
                    <div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                        <Space>
                          <Tag color="blue">{log.type}</Tag>
                          <Text type="secondary" style={{ fontSize: 12 }}>
                            {log.user?.name} · {dayjs(log.serviceAt).format('MM-DD HH:mm')}
                          </Text>
                        </Space>
                        {(user?.role === 'admin' || log.userId === user?.id) && (
                          <Popconfirm title="确认删除？" onConfirm={() => handleDeleteLog(log.id)}>
                            <Button type="link" size="small" danger>删除</Button>
                          </Popconfirm>
                        )}
                      </div>
                      <div style={{ fontSize: 13 }}>{log.content}</div>
                    </div>
                  ),
                }))}
              />
            ) : (
              <div style={{ textAlign: 'center', padding: 24, color: '#ccc' }}>
                <Text type="secondary">暂无服务日志，点击上方按钮添加</Text>
              </div>
            )}

            {/* 编辑按钮 */}
            {(user?.role === 'admin' || user?.role === 'manager') && (
              <div style={{ textAlign: 'right', marginTop: 16, paddingTop: 16, borderTop: '1px solid #f0f0f0' }}>
                <Space>
                  <Button icon={<EditOutlined />} onClick={() => { setDetailOpen(false); openEdit(activeProject); }}>
                    编辑项目
                  </Button>
                  <Button danger onClick={() => { setDetailOpen(false); handleDelete(activeProject.id); }}>
                    删除项目
                  </Button>
                </Space>
              </div>
            )}
          </div>
        )}
      </Modal>

      {/* ===== 服务日志 Modal ===== */}
      <Modal
        title="添加服务日志"
        open={logModalOpen}
        onOk={handleAddLog}
        onCancel={() => setLogModalOpen(false)}
        confirmLoading={logLoading}
        destroyOnClose
      >
        <Form form={logForm} layout="vertical">
          <Form.Item name="type" label="服务类型" rules={[{ required: true, message: '请选择类型' }]}>
            <Select placeholder="选择服务类型">
              {LOG_TYPES.map(t => <Option key={t} value={t}>{t}</Option>)}
            </Select>
          </Form.Item>
          <Form.Item name="content" label="服务内容" rules={[{ required: true, message: '请输入内容' }]}>
            <TextArea rows={4} placeholder="详细描述本次服务内容" />
          </Form.Item>
          <Form.Item name="serviceAt" label="服务时间">
            <DatePicker showTime style={{ width: '100%' }} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
